import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useEffect, useState, useImperativeHandle, forwardRef } from 'react';

import DialogDraw from 'src/components/medicalSchema/TabRecords/DialogDraw';
import DialogTemplate from 'src/components/medicalSchema/TabRecords/DialogTemplate';
import RecordItem from './RecordItem';

import { getAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';
import { useSearchParams } from 'react-router-dom';
import { useNotify } from 'src/context/ContextNotify';

export type TemplateType = {
  id: number;
  name: string;
  categoryid: number;
  thumbnail: string;
};

const Records = forwardRef((props, ref) => {
  const notifyContext = useNotify();
  const [searchParams] = useSearchParams();
  const patientid = searchParams.get('id');
  const [openDialogTemplate, setOpenDialogTemplate] = useState(false);
  const [openDialogDraw, setOpenDialogDraw] = useState(false);
  const [templateActive, setTemplateActive] = useState<TemplateType | null>(
    null
  );
  const [templateDetail, setTemplateDetail] = useState(null);
  const [patient, setPatient] = useState(null);
  const [templates, setTemplates] = useState<TemplateType[]>([]);
  const [schemaList, setSchemaList] = useState([]);
  const [checkAPI, setCheckAPI] = useState(false);
  const [karteEdit, setKarteEdit] = useState({});
  const [isKarteEdit, setIsKarteEdit] = useState(false);
  const [rowSelected, setRowSelected] = useState(0);
  const token = getToken();

  const getTemplates = () => {
    try {
      setCheckAPI(false);
      getAPI(`/patient/view?patientid=${patientid}&token=${token}`)
        .then((res) => {
          if (res.code === 200) {
            setTemplates(res.data.shemas);
            setSchemaList(res.data.kartes);
            setPatient(res.data.patient);
          } else {
            setTemplates([]);
            setSchemaList([]);
            setPatient(null);
          }

          if (res.code === 403 || res.code === 500 || res.code === 501) {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg,
              type: 'error',
            });
          }

          setTemplateActive(null);
        })
        .finally(() => {
          setCheckAPI(true);
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      return;
    }
  };

  useEffect(() => {
    getTemplates();
    // eslint-disable-next-line
  }, []);

  const handleSearchTemplate = (keysearch: string) => {
    getAPI(
      `/master/shema-search?keyword=${keysearch}&status=1&page=1&pageline=100&token=${token}`
    ).then((res) => {
      if (res.code === 200) {
        setTemplates(res.data.items);
      } else {
        setTemplates([]);
      }
      setTemplateActive(null);
    });
  };

  useImperativeHandle(ref, () => ({

    handleCreateTemplate() {
      setOpenDialogTemplate(true);
    },

  }));

  const handleReloadData = () => {
    getTemplates();
  };

  const handleCloseDialogTemplate = () => {
    setOpenDialogTemplate(false);
  };

  const handleSelectedTemplate = () => {
    setIsKarteEdit(false);
    if (templateActive === null) {
      setTemplateDetail(null);
      setOpenDialogTemplate(false);
      setOpenDialogDraw(true);
      return;
    }
    getAPI(`master/shema-detail?id=${templateActive.id}&token=${token}`).then(
      (res) => {
        if (res.code === 200) {
          setTemplateDetail(res.data);
          setOpenDialogTemplate(false);
          setOpenDialogDraw(true);
        }
      }
    );
  };

  const handleCreateDraw = () => {
    setOpenDialogDraw(true);
  };

  const handleCloseDialogDraw = () => {
    setOpenDialogDraw(false);
  };

  const handleBtnReturn = () => {
    setOpenDialogTemplate(true);
    setOpenDialogDraw(false);
  };

  const handleTemplateClick = (template = null) => {
    setTemplateActive(template);
  };

  // const handleDeleteKartes = () => {
  //   getAPI(
  //     `karte/get-patient-kartes?patientid=${patientid}&token=${token}`
  //   ).then((res) => {
  //     if (res.code === 200) {
  //       setSchemaList(res.data.kartes);
  //     }
  //   });
  // };

  const handleEditCanvas = (karteid: number, rowIndex: number) => {
    getAPI(
      `karte/detail?id=${karteid}&patientid=${patientid}&token=${token}`
    ).then((res) => {
      if (res.code === 200) {
        setRowSelected(rowIndex);
        setIsKarteEdit(true);
        setKarteEdit(res.data);
        setOpenDialogDraw(true);
      }
    });
  };

  return (
    <>
      {/* <Stack
        direction={'row'}
        spacing={'26px'}
        p={'8px 24px'}
        border={'1px solid #F3F3F3'}
      >
        <Button
          sx={{
            width: '80px',
            height: '32px',
            lineHeight: '14px',
            fontSize: '14px',
            fontWeight: '700',
            color: 'primary.light',
            p: '6px 11px',
            background: 'linear-gradient(95.64deg, #00C3FF 0%, #0090F8 100%)',
            '&:hover': { opacity: 0.8 },
          }}
          onClick={handleCreateTemplate}
        >
          新規作成
        </Button>
      </Stack> */}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 'none',
        }}
      >
        <Table
          sx={{
            '& .MuiTableCell-root.MuiTableCell-head': {
              borderBottom: '1px solid #F3F3F3',
              fontWeight: 700,
              p: '6px 16px',
              fontSize: '14px',
            },
            '& .MuiTableCell-root.MuiTableCell-body': {
              color: 'primary.dark',
              fontSize: '14px',
              py: '6px',
            },
          }}
          aria-label='simple table'
        >
          <TableHead>
            <TableRow>
              <TableCell align='left'>サムネイル</TableCell>
              <TableCell align='left'>受付日</TableCell>
              <TableCell align='left'>担当者</TableCell>
              <TableCell align='left'>ケース</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {checkAPI ? (
              schemaList.map((row, index) => (
                <RecordItem
                  key={index}
                  row={row}
                  rowIndex={index}
                  // handleDeleteKartes={handleDeleteKartes}
                  handleEditCanvas={handleEditCanvas}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4}>
                  <Stack spacing={1}>
                    <Skeleton variant='rounded' width={'100%'} height={50} />
                    <Skeleton variant='rounded' width={'100%'} height={50} />
                    <Skeleton variant='rounded' width={'100%'} height={50} />
                    <Skeleton variant='rounded' width={'100%'} height={50} />
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <DialogTemplate
        templates={templates}
        openDialogTemplate={openDialogTemplate}
        handleCloseDialogTemplate={handleCloseDialogTemplate}
        handleTemplateClick={handleTemplateClick}
        templateActive={templateActive}
        handleSelectedTemplate={handleSelectedTemplate}
        handleSearchTemplate={handleSearchTemplate}
      />
      <DialogDraw
        openDialogDraw={openDialogDraw}
        handleCreateDraw={handleCreateDraw}
        handleCloseDialogDraw={handleCloseDialogDraw}
        handleBtnReturn={handleBtnReturn}
        handleReloadData={handleReloadData}
        templateActive={templateActive}
        templateDetail={templateDetail}
        karteEdit={karteEdit}
        isKarteEdit={isKarteEdit}
        patient={patient}
        schemaList={schemaList}
        rowSelected={rowSelected}
      />
    </>
  );
});

export default Records;
